import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, ScanSearch, Scissors, Trash2, Sun } from "lucide-react";
import { Button } from "../components/ui/button";
import { API, api, errMsg } from "./api";

const Card = ({ title, children, testid }) => (
  <div className="rounded-lg border border-zinc-800 bg-[#121215] p-4" data-testid={testid}>
    <div className="mb-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{title}</div>
    {children}
  </div>
);

const SectionData = ({ d, onSplit, onSkylight, busy }) => (
  <div className="space-y-4" data-testid="section-data">
    {d.summary && <p className="text-sm text-zinc-300">{d.summary}</p>}
    <div className="grid gap-4 md:grid-cols-2">
      <Card title="Floor levels" testid="section-levels">
        <table className="w-full text-sm"><tbody>
          {d.levels.map((l, i) => (
            <tr key={`${l.name}@${l.elevation_m}`} className="border-t border-zinc-800/70">
              <td className="py-1 text-zinc-300">{l.name}</td>
              <td className="py-1 text-right font-mono text-sky-300">{l.elevation_m >= 0 ? "+" : ""}{l.elevation_m.toFixed(3)} m</td>
              <td className="py-1 text-right font-mono text-xs text-zinc-500">{i ? `Δ ${(l.elevation_m - d.levels[i - 1].elevation_m).toFixed(2)} m` : ""}</td>
            </tr>
          ))}
        </tbody></table>
        <div className="mt-3 flex flex-wrap gap-x-4 font-mono text-[11px] text-zinc-500">
          {d.parapet_top_m != null && <span>Parapet top +{d.parapet_top_m} m</span>}
          {d.slab_thickness_mm != null && <span>Slab {d.slab_thickness_mm} mm</span>}
          {d.slab_edges?.[0] && <span>Slab edge → facade {d.slab_edges[0].offset_mm} mm</span>}
        </div>
      </Card>
      <Card title="Sloped glazing / skylights" testid="section-skylights">
        {d.sloped_glazing?.length ? d.sloped_glazing.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 border-t border-zinc-800/70 py-2 text-sm first:border-0">
            <span className="text-zinc-300">{s.label} · {s.plan_length_m} × {s.plan_width_m} m · {s.slope_deg}°</span>
            <Button data-testid={`section-skylight-design-${s.id}`} size="sm" variant="outline" disabled={busy || !!s.design_id} onClick={() => onSkylight(s)} className="border-zinc-700 text-xs">
              <Sun className="mr-1.5 h-3.5 w-3.5" />{s.design_id ? "Created ✓" : "Skylight design"}
            </Button>
          </div>
        )) : <p className="text-sm text-zinc-500">No sloped glazing found in this section.</p>}
      </Card>
    </div>
    {d.notes && <p className="text-xs text-zinc-500">Notes: {d.notes}</p>}
    <Button data-testid="apply-sections-btn" onClick={onSplit} disabled={busy} className="bg-amber-500 text-black hover:bg-amber-400">
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Scissors className="mr-2 h-4 w-4" />}Split elevation zones at these levels
    </Button>
  </div>
);

export default function SectionsPanel({ project, setProject }) {
  const secs = project.sections || [];
  const [active, setActive] = useState(secs[secs.length - 1]?.id || null);
  const [busy, setBusy] = useState(null);
  const sec = secs.find((s) => s.id === active);
  const reload = async () => setProject((await api.get(`/projects/${project.id}`)).data);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errMsg(e, "Failed")); } finally { setBusy(null); }
  };
  const upload = (file) => run("upload", async () => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/projects/${project.id}/sections`, fd);
    await reload();
    setActive(data.id);
    toast.success("Section uploaded. Click Analyse to read the levels.");
  });
  const analyse = () => run("analyse", async () => { await api.post(`/projects/${project.id}/sections/${sec.id}/analyze`); await reload(); toast.success("Section read"); });
  const remove = () => run("delete", async () => { await api.delete(`/projects/${project.id}/sections/${sec.id}`); await reload(); setActive(null); });
  const split = () => run("split", async () => {
    const { data } = await api.post(`/projects/${project.id}/apply-sections`);
    await reload();
    toast.success(`${data.split} split(s): zones now follow the floor levels`);
  });
  const skylight = (s) => run("sky", async () => {
    const { data } = await api.post(`/projects/${project.id}/takeoff/designs`, { items: [{ kind: "section_skylight", id: s.id, system_type: "skylight" }] });
    if (data.errors.length) throw new Error(data.errors[0].error);
    await reload();
    toast.success(`${data.created[0].name} created in the Systems tab`);
  });

  return (
    <div className="space-y-6" data-testid="sections-panel">
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex cursor-pointer items-center rounded-md bg-sky-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-600" data-testid="section-upload-btn">
          {busy === "upload" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Upload section (PDF / image)
          <input data-testid="section-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
        {secs.map((s) => (
          <button key={s.id} data-testid={`section-chip-${s.id}`} onClick={() => setActive(s.id)} className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${s.id === active ? "border-sky-500/50 bg-sky-500/10 text-sky-300" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>
            {s.filename}{s.analysis ? " ✓" : ""}
          </button>
        ))}
      </div>
      {!secs.length && <p className="max-w-2xl text-sm text-zinc-400" data-testid="sections-empty">Upload building sections. AI reads the floor levels, slab edges, parapet and any sloped glazing. You can then split the elevation zones at real floor-to-floor heights, size slab-edge brackets, and create skylight designs.</p>}
      {sec && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <div className="space-y-3">
            <img data-testid="section-image" src={`${API}/projects/${project.id}/sections/${sec.id}/image`} alt={sec.filename} className="w-full rounded-lg border border-zinc-800 bg-white" />
            <div className="flex gap-2">
              <Button data-testid="analyze-section-btn" onClick={analyse} disabled={!!busy} className="bg-sky-500 text-white hover:bg-sky-600">
                {busy === "analyse" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanSearch className="mr-2 h-4 w-4" />}{sec.analysis ? "Re-analyse" : "AI analyse section"}
              </Button>
              <Button data-testid="delete-section-btn" variant="outline" onClick={remove} disabled={!!busy} className="border-zinc-700"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
          {sec.analysis ? <SectionData d={{ ...sec.analysis, sloped_glazing: (project.section_data?.section_id === sec.id ? project.section_data.sloped_glazing : sec.analysis.sloped_glazing) }} onSplit={split} onSkylight={skylight} busy={!!busy} />
            : <p className="text-sm text-zinc-500">Not analysed yet.</p>}
        </div>
      )}
    </div>
  );
}
